import { useEffect, useRef } from 'react';
import { formatDateTime } from '../../lib/labels';
import type { TimelineMessage } from '../../types/dashboard';
import { openEngineEvidenceUrl } from '../EvidenceList';

interface Props {
  timeline: TimelineMessage[];
  token: string;
}

interface AttachmentChip {
  label: string;
  evidenceId: string | null;
  note: string | null;
}

/** Inbound attachments (texted MMS media / emailed files) → chips.
    evidence_id present = mirrored into case storage and openable; absent =
    still mirroring, unsupported type, or mirror failed — shown either way
    so an arrived attachment is never invisible. */
function attachmentChips(m: TimelineMessage): AttachmentChip[] {
  const meta = (m.metadata ?? {}) as Record<string, unknown>;
  const chips: AttachmentChip[] = [];
  for (const [i, item] of ((meta.media as Array<Record<string, unknown>>) ?? []).entries()) {
    const type = String(item.content_type ?? '');
    chips.push({
      label: type.startsWith('image/') ? `Photo ${i + 1}` : `Attachment ${i + 1}`,
      evidenceId: (item.evidence_id as string) ?? null,
      note: item.unsupported ? `${type || 'unknown type'} — not viewable here` : item.evidence_id ? null : 'processing…',
    });
  }
  for (const item of (meta.attachments as Array<Record<string, unknown>>) ?? []) {
    chips.push({
      label: String(item.filename ?? 'attachment'),
      evidenceId: (item.evidence_id as string) ?? null,
      note: item.evidence_id ? null : 'not viewable here',
    });
  }
  return chips;
}

// ── Voice call rendering ──────────────────────────────────────────────────
// Phone calls land as 'voice' messages whose body is a "📞 <headline>" line
// followed by a Deepgram transcript (speaker-labelled "Caller: …" / "Sarah: …"
// turns for a conference, or a single speaker for a voicemail). Render them as
// a distinct call card rather than a plain text bubble.

interface VoiceTurn {
  speaker: string | null;
  text: string;
}

function parseVoiceBody(body: string): { headline: string | null; turns: VoiceTurn[] } {
  const lines = body.split('\n');
  let headline: string | null = null;
  let rest = lines;
  if (lines[0]?.startsWith('📞')) {
    headline = lines[0].replace(/^📞\s*/, '').trim();
    rest = lines.slice(1);
  }
  const turns = rest
    .filter((l) => l.trim().length > 0)
    .map<VoiceTurn>((l) => {
      const match = l.match(/^([A-Za-z][\w .'-]{0,24}):\s+(.*)$/);
      return match ? { speaker: match[1], text: match[2] } : { speaker: null, text: l };
    });
  return { headline, turns };
}

function formatDuration(secs: unknown): string | null {
  const n = typeof secs === 'number' ? secs : null;
  if (!n || n <= 0) return null;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function VoiceCard({ m }: { m: TimelineMessage }) {
  const meta = (m.metadata ?? {}) as Record<string, unknown>;
  const { headline, turns } = parseVoiceBody(m.body);
  const duration = formatDuration(meta.duration_seconds);
  const viaAgent = meta.voice_agent === true || /voice agent/i.test(headline ?? '');
  const hasRecording = typeof meta.recording_url === 'string' && (meta.recording_url as string).length > 0;
  const title = headline?.replace(/\s*\(via voice agent\)\s*$/i, '') || 'Phone call';
  const partyName = m.thread?.parties?.name ?? 'Unknown party';

  return (
    <li className="flex justify-start">
      <div className="w-full max-w-[92%] rounded-lg border border-surface-border bg-surface-muted p-3">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-subtle text-xs text-brand">📞</span>
          <span className="font-semibold text-gray-700">{title}</span>
          {duration && (
            <span className="rounded-full border border-surface-border bg-white px-1.5 py-0.5 font-medium tabular-nums">
              {duration}
            </span>
          )}
          {viaAgent && (
            <span className="rounded-full bg-brand-subtle px-1.5 py-0.5 font-medium text-brand">voice agent</span>
          )}
          <span className="ml-auto">
            {partyName} · {formatDateTime(m.created_at)}
          </span>
        </div>

        {turns.length > 0 ? (
          <div className="mt-2 space-y-1.5">
            {turns.map((t, i) => {
              const isCaller = t.speaker != null && /caller/i.test(t.speaker);
              return (
                <p key={i} className="text-sm leading-snug text-gray-800">
                  {t.speaker && (
                    <span className={`mr-1.5 font-semibold ${isCaller ? 'text-gray-600' : 'text-brand'}`}>
                      {t.speaker}:
                    </span>
                  )}
                  {t.text}
                </p>
              );
            })}
          </div>
        ) : (
          <p className="mt-2 text-sm italic text-gray-500">No transcript was captured for this call.</p>
        )}

        {hasRecording && (
          <div className="mt-2">
            <span
              className="inline-flex items-center gap-1 rounded-full border border-surface-border bg-white px-2 py-0.5 text-[11px] text-gray-500"
              title="Audio recording was captured and stored"
            >
              🎧 Recording captured
            </span>
          </div>
        )}
      </div>
    </li>
  );
}

const FAILED_STATUSES = new Set(['failed', 'bounced', 'suppressed']);

const statusLabel = (status: string): string => {
  switch (status) {
    case 'received':
      return 'Received';
    case 'queued':
      return 'Queued';
    case 'sending':
      return 'Sending…';
    case 'sent':
      return 'Sent';
    case 'delivered':
      return 'Delivered ✓';
    case 'failed':
      return 'Failed ✕';
    case 'bounced':
      return 'Bounced ✕';
    case 'suppressed':
      return 'Suppressed (opt-out)';
    default:
      return status;
  }
};

/**
 * Merged per-case conversation: every party thread interleaved in time
 * order, outbound bubbles carrying true delivery status (delivery truth,
 * not delivery hope — failed sends are loud and red).
 */
export function TimelinePanel({ timeline, token }: Props) {
  // Open on the newest messages — jump the scroll container to the bottom when
  // the conversation loads or a new message lands (Danny, 2026-10-22).
  const listRef = useRef<HTMLOListElement | null>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [timeline.length]);

  if (timeline.length === 0) {
    return (
      <p className="p-4 text-sm text-gray-500">
        No messages yet. Use the composer below to reach a party.
      </p>
    );
  }

  return (
    <ol ref={listRef} className="max-h-[28rem] space-y-3 overflow-y-auto p-3">
      {timeline.map((m) => {
        if (m.channel === 'voice') return <VoiceCard key={m.id} m={m} />;
        const outbound = m.direction === 'outbound';
        const failed = FAILED_STATUSES.has(m.status);
        const partyName = m.thread?.parties?.name ?? 'Unknown party';
        return (
          <li key={m.id} className={outbound ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={[
                'max-w-[85%] rounded-lg border p-2.5',
                outbound
                  ? failed
                    ? 'border-red-300 bg-red-50'
                    : 'border-brand-subtle bg-brand-subtle'
                  : 'border-surface-border bg-white',
              ].join(' ')}
            >
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                <span className="font-medium text-gray-700">
                  {outbound ? `To ${partyName}` : `From ${partyName}`}
                </span>
                <span className="uppercase">{m.channel}</span>
                <span>{formatDateTime(m.created_at)}</span>
              </div>
              {m.subject && <div className="mt-1 text-xs font-semibold">{m.subject}</div>}
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{m.body}</p>
              {attachmentChips(m).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {attachmentChips(m).map((chip, i) =>
                    chip.evidenceId ? (
                      <button
                        key={i}
                        type="button"
                        className="rounded-full border border-brand-subtle bg-white px-2 py-0.5 text-[11px] font-medium text-brand hover:bg-brand-subtle"
                        title="Open (view-only, from case storage)"
                        onClick={() =>
                          void openEngineEvidenceUrl(`/api/v1/evidence/${chip.evidenceId}/file`, chip.label, token)
                        }
                      >
                        📎 {chip.label}
                      </button>
                    ) : (
                      <span
                        key={i}
                        className="rounded-full border border-surface-border bg-surface-muted px-2 py-0.5 text-[11px] text-gray-500"
                        title={chip.note ?? undefined}
                      >
                        📎 {chip.label}
                        {chip.note ? ` · ${chip.note}` : ''}
                      </span>
                    ),
                  )}
                </div>
              )}
              {outbound && (
                <div
                  className={[
                    'mt-1 text-[11px] font-medium',
                    failed ? 'text-red-700' : m.status === 'delivered' ? 'text-green-700' : 'text-gray-500',
                  ].join(' ')}
                >
                  {statusLabel(m.status)}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
